import {
  ShoppingCart as CartIcon,
  Close as CloseIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Favorite as FavoriteIcon,
  CheckCircle as InStockIcon,
  Cancel as OutOfStockIcon,
  LocalShipping as ShippingIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogContent,
  Divider,
  Grid,
  IconButton,
  Skeleton,
  Typography,
  alpha,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import { Navigation, Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import CurrencyDisplay from '@shared/components/CurrencyDisplay';
import { useCart } from '../../context/CartContext';
import { useFavorites } from '../../context/FavoritesContext';
import { useI18n } from '../../context/I18nContext';
import productService from '../../services/productService';
import { sanitizeImageUrl } from '../../utils/image';
import { sanitizeHtml } from '../../utils/sanitizeHtml';

const QuickViewModal = ({ open, onClose, productId, productSlug }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const navigate = useNavigate();
  const { addToCart } = useCart();
  const { isFavorite, toggleFavorite } = useFavorites();
  const { locale } = useI18n();

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [quantity] = useState(1);
  const [addingToCart, setAddingToCart] = useState(false);

  // Map API product data - MUST be defined before useEffect that uses it
  const mapApiProduct = useCallback(
    (p) => {
      const nameObj = p.name || { ru: p.name_ru, en: p.name_en, ky: p.name_ky };
      const descObj = p.description || {
        ru: p.description_ru,
        en: p.description_en,
        ky: p.description_ky,
      };
      const gallery = Array.isArray(p.images?.urls)
        ? p.images.urls.map((url) => sanitizeImageUrl(url)).filter(Boolean)
        : Array.isArray(p.imageGallery)
          ? p.imageGallery.map((url) => sanitizeImageUrl(url)).filter(Boolean)
          : [];

      // Add main image to gallery if not already there
      const mainImage =
        sanitizeImageUrl(p.images?.thumbnailUrl) ||
        sanitizeImageUrl(p.imageUrl) ||
        sanitizeImageUrl(p.image);
      if (mainImage && !gallery.includes(mainImage)) {
        gallery.unshift(mainImage);
      }

      return {
        id: p.id || p._id,
        slug: p.slug,
        sku: p.sku,
        name: nameObj,
        description: descObj,
        price: p.pricing?.kgs ?? p.price_kgs ?? p.price ?? 0,
        originalPrice: p.pricing?.original ?? p.originalPrice,
        discount: p.discount || 0,
        stock: p.stock?.quantity ?? p.stock_quantity ?? 0,
        images: gallery,
        category:
          typeof p.categoryName === 'object'
            ? p.categoryName[locale] || p.categoryName.ru || p.categoryName.en
            : p.category,
        freeShipping: p.freeShipping || p.price >= 5000,
      };
    },
    [locale]
  );

  // Fetch product data
  useEffect(() => {
    if (!open || (!productId && !productSlug)) return;

    const fetchProduct = async () => {
      setLoading(true);
      setError(null);
      try {
        const slug = productSlug || productId;
        const res = await productService.getProduct(slug);
        const data = res.data?.product || res.data;
        setProduct(mapApiProduct(data));
      } catch (e) {
        console.error('Quick view fetch error:', e);
        setError('Не удалось загрузить товар');
      } finally {
        setLoading(false);
      }
    };

    fetchProduct();
  }, [open, productId, productSlug, mapApiProduct]);

  // Get localized text
  const getName = () => {
    if (!product?.name) return '';
    return typeof product.name === 'object'
      ? product.name[locale] || product.name.ru || product.name.en || ''
      : product.name;
  };

  const getDescription = () => {
    if (!product?.description) return '';
    const desc =
      typeof product.description === 'object'
        ? product.description[locale] ||
          product.description.ru ||
          product.description.en ||
          ''
        : product.description;
    // Sanitize HTML to prevent XSS
    return sanitizeHtml(desc);
  };

  // Handlers
  const handleAddToCart = async () => {
    if (!product) return;
    setAddingToCart(true);
    try {
      await addToCart(product, quantity);
      onClose();
    } catch (e) {
      console.error('Add to cart error:', e);
    } finally {
      setAddingToCart(false);
    }
  };

  const handleToggleFavorite = () => {
    if (product?.id) {
      toggleFavorite(product.id);
    }
  };

  const handleViewDetails = () => {
    onClose();
    navigate(`/products/${product?.slug || productSlug || productId}`);
  };

  const favorite = product?.id ? isFavorite(product.id) : false;
  const inStock = product?.stock > 0;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          maxHeight: isMobile ? '100%' : '90vh',
        },
      }}
    >
      {/* Close button */}
      <IconButton
        onClick={onClose}
        aria-label="Закрыть"
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          zIndex: 10,
          bgcolor: 'background.paper',
          boxShadow: 1,
          minWidth: 44,
          minHeight: 44,
          '&:hover': { bgcolor: 'grey.100' },
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ p: { xs: 2, md: 3 } }}>
        {loading ? (
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Skeleton
                variant="rectangular"
                height={300}
                sx={{ borderRadius: 2 }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <Skeleton variant="text" height={40} width="80%" />
              <Skeleton variant="text" height={30} width="40%" />
              <Skeleton variant="text" height={100} />
              <Skeleton
                variant="rectangular"
                height={48}
                sx={{ mt: 2, borderRadius: 1 }}
              />
            </Grid>
          </Grid>
        ) : error ? (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography color="error">{error}</Typography>
            <Button onClick={onClose} sx={{ mt: 2 }}>
              Закрыть
            </Button>
          </Box>
        ) : product ? (
          <Grid container spacing={3}>
            {/* Images */}
            <Grid item xs={12} md={6}>
              <Box sx={{ position: 'relative' }}>
                {/* Discount badge */}
                {product.discount > 0 && (
                  <Chip
                    label={`-${product.discount}%`}
                    size="small"
                    sx={{
                      position: 'absolute',
                      top: 12,
                      left: 12,
                      zIndex: 5,
                      bgcolor: '#ff1744',
                      color: 'white',
                      fontWeight: 700,
                    }}
                  />
                )}

                {product.images?.length > 1 ? (
                  <Swiper
                    modules={[Navigation, Pagination]}
                    navigation
                    pagination={{ clickable: true }}
                    style={{ borderRadius: 12 }}
                  >
                    {product.images.map((img, idx) => (
                      <SwiperSlide key={idx}>
                        <Box
                          component="img"
                          src={img}
                          alt={`${getName()} - ${idx + 1}`}
                          sx={{
                            width: '100%',
                            height: { xs: 280, md: 350 },
                            objectFit: 'contain',
                            bgcolor: 'grey.50',
                            borderRadius: 2,
                          }}
                        />
                      </SwiperSlide>
                    ))}
                  </Swiper>
                ) : (
                  <Box
                    component="img"
                    src={
                      product.images?.[0] || '/images/placeholder-product.svg'
                    }
                    alt={getName()}
                    sx={{
                      width: '100%',
                      height: { xs: 280, md: 350 },
                      objectFit: 'contain',
                      bgcolor: 'grey.50',
                      borderRadius: 2,
                    }}
                  />
                )}
              </Box>
            </Grid>

            {/* Product Info */}
            <Grid item xs={12} md={6}>
              {/* Category */}
              {product.category && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
                >
                  {product.category}
                </Typography>
              )}

              {/* Name */}
              <Typography
                variant="h5"
                fontWeight={700}
                sx={{ mt: 0.5, mb: 1, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
              >
                {getName()}
              </Typography>

              {/* SKU */}
              {product.sku && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: 'block', mb: 2, overflowWrap: 'anywhere', wordBreak: 'break-word' }}
                >
                  Артикул: {product.sku}
                </Typography>
              )}

              {/* Price */}
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'baseline',
                  gap: 1.5,
                  mb: 2,
                }}
              >
                <CurrencyDisplay
                  amount={product.price}
                  variant="h4"
                  fontWeight={800}
                  color="primary"
                />
                {product.originalPrice &&
                  product.originalPrice > product.price && (
                    <Typography
                      variant="body1"
                      sx={{
                        textDecoration: 'line-through',
                        color: 'text.disabled',
                      }}
                    >
                      <CurrencyDisplay amount={product.originalPrice} />
                    </Typography>
                  )}
              </Box>

              {/* Stock Status */}
              <Box sx={{ mb: 2 }}>
                {inStock ? (
                  <Chip
                    icon={<InStockIcon />}
                    label={`В наличии: ${product.stock} шт`}
                    size="small"
                    sx={{
                      bgcolor: alpha('#4caf50', 0.1),
                      color: '#2e7d32',
                      '& .MuiChip-icon': { color: '#4caf50' },
                    }}
                  />
                ) : (
                  <Chip
                    icon={<OutOfStockIcon />}
                    label="Нет в наличии"
                    size="small"
                    sx={{
                      bgcolor: alpha('#f44336', 0.1),
                      color: '#c62828',
                      '& .MuiChip-icon': { color: '#f44336' },
                    }}
                  />
                )}
                {product.freeShipping && (
                  <Chip
                    icon={<ShippingIcon />}
                    label="Бесплатная доставка"
                    size="small"
                    sx={{
                      ml: 1,
                      bgcolor: alpha('#2196f3', 0.1),
                      color: '#1565c0',
                      '& .MuiChip-icon': { color: '#2196f3' },
                    }}
                  />
                )}
              </Box>

              {/* Description */}
              {getDescription() && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    component="div"
                    dangerouslySetInnerHTML={{ __html: getDescription() }}
                    sx={{
                      mb: 2,
                      '& h3': {
                        mt: 1.5,
                        mb: 0.5,
                        fontSize: '0.95rem',
                        fontWeight: 600,
                      },
                      '& ul': { pl: 2, mb: 1 },
                      '& li': { mb: 0.25 },
                      '& p': { mb: 1 },
                    }}
                  />
                </>
              )}

              {/* Actions */}
              <Box sx={{ display: 'flex', gap: 1.5, mt: 3 }}>
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<CartIcon />}
                  onClick={handleAddToCart}
                  disabled={!inStock || addingToCart}
                  sx={{ py: 1.5, minHeight: 44 }}
                >
                  {addingToCart ? 'Добавление...' : 'В корзину'}
                </Button>
                <IconButton
                  onClick={handleToggleFavorite}
                  aria-label={favorite ? 'Убрать из избранного' : 'Добавить в избранное'}
                  sx={{
                    border: 1,
                    borderColor: favorite ? 'error.main' : 'divider',
                    color: favorite ? 'error.main' : 'text.secondary',
                    minWidth: 44,
                    minHeight: 44,
                    '&:hover': {
                      borderColor: 'error.main',
                      color: 'error.main',
                    },
                  }}
                >
                  {favorite ? <FavoriteIcon /> : <FavoriteBorderIcon />}
                </IconButton>
              </Box>

              {/* View details button */}
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ViewIcon />}
                onClick={handleViewDetails}
                sx={{ mt: 2, minHeight: 44 }}
              >
                Подробнее о товаре
              </Button>
            </Grid>
          </Grid>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default QuickViewModal;
